/**
 * Amounts for the Accounts console — decimal digit strings, validated and
 * scaled with `BigInt`, never a JavaScript `Number`.
 *
 * Server-side balances are `BigInteger` and may legitimately exceed 2^53, where
 * a `Number` silently rounds (`9007199254740993` becomes `9007199254740992`).
 * A rounded balance shown to an operator is a wrong balance; a rounded amount
 * submitted is a wrong grant. So no amount in this console is ever produced by,
 * or passed through, `Number()`, `parseInt`, `parseFloat`, or arithmetic on a
 * `number` — including the response-parsing step (see `bigintJson.ts`).
 *
 * The two magnitudes in play are different and are not interchangeable:
 * `10^15` (the submittable bound) is *smaller* than `2^53`, so an accepted
 * input can never round. The `BigInt` discipline on the input path buys digit
 * fidelity and exact bound comparison; on the read path it buys correctness.
 */

/** Inclusive upper bound for a single operator write (design D18), as digits. */
export const AMOUNT_MAX = '1000000000000000' // 10^15

const AMOUNT_MAX_BIG = BigInt(AMOUNT_MAX)

export type AmountRejection =
  | 'empty'
  | 'not_digits'
  | 'leading_zero'
  | 'not_positive'
  | 'above_max'

export interface AmountValid {
  ok: true
  /** The operator's digits, verbatim — never normalized or re-spelled. */
  value: string
}

export interface AmountInvalid {
  ok: false
  code: AmountRejection
  message: string
}

export type AmountValidation = AmountValid | AmountInvalid

const LEADING_ZERO_MESSAGE =
  'Amount must be a positive integer written without a leading zero.'

/**
 * Validate a grant/reversal amount: digits only, no sign, no separator, no
 * decimal point, no leading zero, strictly positive, at most 10^15 inclusive.
 *
 * Deliberately strict rather than forgiving: normalizing `007` to `7` would let
 * the input field and the request body each hold a different spelling of the
 * operator's input, and a padded amount is far more often a typo or a paste
 * artifact than an intent.
 */
export function validateAmountInput(raw: string): AmountValidation {
  if (raw === '' || raw == null) {
    return { ok: false, code: 'empty', message: 'Amount is required.' }
  }
  if (!/^\d+$/.test(raw)) {
    return {
      ok: false,
      code: 'not_digits',
      message: 'Amount must contain digits only — no sign, separator, or decimal point.',
    }
  }
  if (raw.length > 1 && raw.startsWith('0')) {
    return { ok: false, code: 'leading_zero', message: LEADING_ZERO_MESSAGE }
  }
  const value = BigInt(raw)
  if (value <= 0n) {
    return { ok: false, code: 'not_positive', message: LEADING_ZERO_MESSAGE }
  }
  if (value > AMOUNT_MAX_BIG) {
    return {
      ok: false,
      code: 'above_max',
      message: `Amount must be at most ${AMOUNT_MAX} units of the bucket's smallest unit.`,
    }
  }
  return { ok: true, value: raw }
}

/**
 * Decimal exponents, mirroring the backend's single source of truth:
 * `backend/app/payment/utils/currency.py` → `_CURRENCY_EXPONENTS`.
 *
 * round-1 fix (F3, category validation). This table previously carried a
 * hand-written ISO-flavoured list in which `IDR` was 0, while the backend —
 * which is what actually stores and scales these amounts — has `IDR: 2`. The
 * console therefore rendered every Indonesian balance 100× too large: `12345`
 * showed as `12345 IDR` instead of `123.45 IDR`, in the list, in the balance
 * cards, and in the ledger's before/after columns.
 *
 * This is NOT a general ISO 4217 table and must not be extended as one. Its key
 * set is exactly the platform's provider-supported currency union; a currency no
 * provider can charge has no bucket to display. `amounts.test.ts` parses the
 * Python module and asserts this object equals it key-for-key and value-for-
 * value, so the two cannot drift: onboarding a currency backend-side fails the
 * frontend suite until this table is updated in the same change.
 */
export const CURRENCY_EXPONENTS: Readonly<Record<string, number>> = Object.freeze({
  AUD: 2,
  CAD: 2,
  CHF: 2,
  CNY: 2,
  DKK: 2,
  EUR: 2,
  GBP: 2,
  HKD: 2,
  IDR: 2,
  INR: 2,
  JPY: 0,
  KRW: 0,
  MYR: 2,
  NOK: 2,
  NZD: 2,
  PHP: 2,
  SEK: 2,
  SGD: 2,
  THB: 2,
  TWD: 2,
  USD: 2,
  VND: 0,
})

export interface BucketAsset {
  asset_kind: string
  currency: string | null
}

/**
 * Scale an integer digit string for display, using `BigInt` integer division
 * and remainder — never by dividing a `number` by a power of ten.
 *
 * - a `virtual` bucket (`currency: null`) is its own atomic unit: the digits
 *   are shown as they arrived, with no separator and no currency substituted;
 * - a `monetary` bucket with a known exponent is split into integer and
 *   fractional parts over the digit string;
 * - a `monetary` bucket whose currency the table does not contain is shown
 *   unscaled in its smallest unit, labelled with the code — no two-decimal
 *   default and no other guess.
 */
export function formatAmount(digits: string, bucket: BucketAsset): string {
  const negative = digits.startsWith('-')
  const magnitude = negative ? digits.slice(1) : digits
  if (!/^\d+$/.test(magnitude)) return digits

  if (bucket.asset_kind !== 'monetary' || bucket.currency == null) {
    return digits
  }
  const exponent = CURRENCY_EXPONENTS[bucket.currency]
  if (exponent === undefined) {
    // Unknown currency: the honest rendering is the raw minor-unit integer
    // plus the code, so the operator can see which asset it is.
    return `${digits} ${bucket.currency}`
  }
  if (exponent === 0) {
    return `${digits} ${bucket.currency}`
  }
  const scale = BigInt('1' + '0'.repeat(exponent))
  const value = BigInt(magnitude)
  const whole = value / scale
  const fraction = value % scale
  const fractionDigits = fraction.toString().padStart(exponent, '0')
  return `${negative ? '-' : ''}${whole.toString()}.${fractionDigits} ${bucket.currency}`
}

/** Compare two digit strings exactly (used for assertions and sorting). */
export function amountEquals(a: string, b: string): boolean {
  if (!/^-?\d+$/.test(a) || !/^-?\d+$/.test(b)) return a === b
  return BigInt(a) === BigInt(b)
}

// ---------------------------------------------------------------------------
// Major-unit input (grant-dialog-monetary-bonus-target, design D5)
// ---------------------------------------------------------------------------
//
// `1000 是一千美元还是十美元` is the confusion that put 10000000 micro-USD into
// a bucket nothing downstream spends. So for a monetary target whose exponent
// this console knows, the operator types the currency's MAJOR unit (`10.00`)
// and the console converts it to the minor-unit integer (`1000`) it sends.
//
// The conversion is pure decimal-string work plus `BigInt` for the positivity
// and bound comparisons: right-pad the fractional digits to exactly the
// exponent and concatenate. There is no multiplication, no division, and no
// `Number` anywhere on this path — `PaymentsPage.tsx::formatAmount`'s
// `Number(x)/100` idiom is a deliberate anti-analog and is not copied.

/**
 * The exponent to enter this bucket's amounts in, or `null` when the bucket has
 * no major unit this console may assume.
 *
 * `null` means "enter the unscaled integer of the bucket's own smallest unit":
 * a virtual bucket (its atomic unit is its own unit) and a monetary bucket
 * whose currency `CURRENCY_EXPONENTS` does not contain. Guessing two decimals
 * for an unknown currency would be a wrong *grant*, which is strictly worse
 * than the wrong label the display path already refuses to risk.
 *
 * `0` is a real answer, not an absent one: JPY/KRW/VND have no fractional unit,
 * and the input path rejects a decimal point for them rather than truncating.
 */
export function majorUnitExponent(bucket: BucketAsset): number | null {
  if (bucket.asset_kind !== 'monetary' || bucket.currency == null) return null
  const exponent = CURRENCY_EXPONENTS[bucket.currency]
  return exponent === undefined ? null : exponent
}

export type MajorUnitRejection =
  | 'empty'
  | 'whitespace'
  | 'sign'
  | 'separator'
  | 'exponent_notation'
  | 'second_point'
  | 'trailing_point'
  | 'no_fraction_allowed'
  | 'not_digits'
  | 'leading_zero'
  | 'too_many_fraction_digits'
  | 'not_positive'
  | 'above_max'

export interface MajorUnitValid {
  ok: true
  /** The minor-unit integer digit string that will go on the wire. */
  value: string
}

export interface MajorUnitInvalid {
  ok: false
  code: MajorUnitRejection
  message: string
}

export type MajorUnitValidation = MajorUnitValid | MajorUnitInvalid

function reject(code: MajorUnitRejection, message: string): MajorUnitInvalid {
  return { ok: false, code, message }
}

/**
 * Parse a major-unit decimal string into the minor-unit integer digit string.
 *
 * Every rejection carries its own code and its own message naming the violated
 * rule: nothing is silently normalized, rounded, truncated, or stripped.
 */
export function parseMajorUnitInput(
  raw: string,
  exponent: number
): MajorUnitValidation {
  if (raw == null || raw === '') {
    return reject('empty', '金额必填。')
  }
  if (/\s/.test(raw)) {
    return reject('whitespace', '金额不能包含空白字符。')
  }
  if (/^[+-]/.test(raw)) {
    return reject('sign', '金额不能带正负号，赠送金额必须为正数。')
  }
  // Comma, underscore and apostrophe are the separators that survive the
  // whitespace check above (which already catches NBSP and thin spaces).
  if (/[,_']/.test(raw)) {
    return reject('separator', '金额不能包含千分位分隔符。')
  }
  if (/[eE]/.test(raw)) {
    return reject('exponent_notation', '金额不能使用科学计数法。')
  }

  const points = raw.split('.').length - 1
  if (points > 1) {
    return reject('second_point', '金额最多只能有一个小数点。')
  }
  if (points === 1 && raw.endsWith('.')) {
    return reject('trailing_point', '小数点后至少要写一位数字。')
  }
  if (points === 1 && exponent === 0) {
    return reject('no_fraction_allowed', '该币种没有更小的单位，不能输入小数点。')
  }

  const [integerPart, fractionPart = ''] = raw.split('.')
  if (!/^\d+$/.test(integerPart)) {
    return reject('not_digits', '金额只能由数字与一个小数点组成。')
  }
  if (fractionPart !== '' && !/^\d+$/.test(fractionPart)) {
    return reject('not_digits', '金额只能由数字与一个小数点组成。')
  }
  if (integerPart.length > 1 && integerPart.startsWith('0')) {
    return reject('leading_zero', '整数部分不能有多余的前导零。')
  }
  if (fractionPart.length > exponent) {
    return reject(
      'too_many_fraction_digits',
      `该币种最多 ${exponent} 位小数，不会四舍五入或截断。`
    )
  }

  // Pure string work: pad the fraction to exactly `exponent` digits, then
  // concatenate. No arithmetic on a `number` at any point.
  const padded = fractionPart + '0'.repeat(exponent - fractionPart.length)
  const digits = (integerPart + padded).replace(/^0+(?=\d)/, '')

  const value = BigInt(digits)
  if (value <= 0n) {
    return reject('not_positive', '金额必须大于 0。')
  }
  if (value > AMOUNT_MAX_BIG) {
    return reject(
      'above_max',
      `换算后的最小单位金额不得超过 ${AMOUNT_MAX}。`
    )
  }
  return { ok: true, value: digits }
}

/**
 * The pre-submit echo: the exact integer that will be sent, in the target's own
 * terms, so the conversion is auditable by the operator accountable for it.
 */
export function describeWireAmount(minorDigits: string, bucket: BucketAsset): string {
  if (bucket.asset_kind === 'monetary' && bucket.currency != null) {
    const exponent = majorUnitExponent(bucket)
    if (exponent === null || exponent === 0) {
      return `将发送 ${minorDigits}（${bucket.currency} 最小单位）`
    }
    return `将发送 ${minorDigits}（${bucket.currency} 最小单位，即 ${formatAmount(
      minorDigits,
      bucket
    )}）`
  }
  return `将发送 ${minorDigits}（该桶的原子单位）`
}

/** The label the amount field carries for a given target (or none selected). */
export function amountUnitLabel(bucket: BucketAsset | null): string {
  if (bucket === null) return '金额（先选择目标）'
  const exponent = majorUnitExponent(bucket)
  if (exponent === null) {
    if (bucket.asset_kind === 'monetary' && bucket.currency != null) {
      return `金额（${bucket.currency} 最小单位整数）`
    }
    return '金额（该桶原子单位整数）'
  }
  if (exponent === 0) {
    return `金额（${bucket.currency}，该币种无小数位）`
  }
  return `金额（${bucket.currency}，主单位，如 10.00）`
}
